// Typed error taxonomy mapped from the havik-streams playback outcome table.

export type PlaybackErrorCode =
  | 'INVALID_URN' // 400 — malformed match URN (terminal)
  | 'NOT_FOUND' // 404 — unknown URN OR not entitled, indistinguishable (terminal)
  | 'GONE' // 410 — ended past the catchup window (terminal)
  | 'TOO_EARLY' // 425 — upcoming, session not upserted yet (retry honoring Retry-After)
  | 'UNAVAILABLE' // 503 — should-be-live but origin failing (retry with backoff)
  | 'UNAUTHORIZED' // 401 — bad/absent key (terminal)
  | 'FORBIDDEN' // 403 — origin not allowed / DRM signature rejected (terminal)
  | 'DRM_CLIENT' // client-side DRM failure: key system unusable on this platform, CDM/EME error, or license transport exhausted — NOT a server entitlement denial (httpStatus carries the license status when there was one, else 0)
  | 'RATE_LIMITED' // 429 — per-IP limiter (back off Retry-After)
  | 'INTERNAL' // 5xx other / unexpected (terminal)
  | 'NETWORK' // fetch threw (transient — retryable in the live-poll loop)
  | 'TIMEOUT' // SDK gave up waiting for live (terminal)
  | 'ABORTED'; // caller aborted via AbortSignal (terminal)

export interface PlaybackErrorInit {
  retryAfterMs?: number;
  requestId?: string;
  /** The raw server-provided error code string, if any — forward-compatible with new server codes. */
  serverCode?: string;
  /**
   * Scheduled kickoff time (epoch ms), if the server included it on a TOO_EARLY
   * response body. The SDK uses this to widen poll cadence for far-future
   * matches without a separate catalog call.
   */
  liveStartsAtMs?: number;
  cause?: unknown;
}

export class PlaybackError extends Error {
  readonly code: PlaybackErrorCode;
  readonly httpStatus: number;
  /** Present for TOO_EARLY / RATE_LIMITED / UNAVAILABLE when the server sent Retry-After. */
  readonly retryAfterMs?: number;
  /** Server request id, when surfaced — useful for correlating opaque 403/503 in server logs. */
  readonly requestId?: string;
  /** Raw server error code (e.g. "TOO_EARLY"), preserved even if `code` maps it to a known enum. */
  readonly serverCode?: string;
  /**
   * Scheduled kickoff time (epoch ms), if the server carried `liveStartsAt` on
   * a TOO_EARLY body. `pollToLive` reads this to widen poll cadence for
   * far-future matches.
   */
  readonly liveStartsAtMs?: number;

  constructor(
    code: PlaybackErrorCode,
    httpStatus: number,
    message: string,
    init?: PlaybackErrorInit,
  ) {
    super(message);
    this.name = 'PlaybackError';
    this.code = code;
    this.httpStatus = httpStatus;
    this.retryAfterMs = init?.retryAfterMs;
    this.requestId = init?.requestId;
    this.serverCode = init?.serverCode;
    this.liveStartsAtMs = init?.liveStartsAtMs;
    if (init?.cause !== undefined) {
      (this as { cause?: unknown }).cause = init.cause;
    }
  }
}

/** Map an HTTP status from /v1/playback to a typed code. */
export function classifyStatus(status: number): PlaybackErrorCode {
  switch (status) {
    case 400:
      return 'INVALID_URN';
    case 401:
      return 'UNAUTHORIZED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 410:
      return 'GONE';
    case 425:
      return 'TOO_EARLY';
    case 429:
      return 'RATE_LIMITED';
    case 503:
      return 'UNAVAILABLE';
    default:
      return 'INTERNAL';
  }
}

/**
 * Map the HTTP status of a DRM license/certificate exchange to a typed code.
 * Only a genuine server auth/entitlement denial keeps the server taxonomy;
 * everything else (no response, transport exhausted, CDM/EME failure) is a
 * client-side DRM failure. Shared by both engines so they cannot drift.
 */
export function classifyDrmStatus(status: number): PlaybackErrorCode {
  return status === 401 ? 'UNAUTHORIZED' : status === 403 ? 'FORBIDDEN' : 'DRM_CLIENT';
}

const WAITABLE: ReadonlySet<PlaybackErrorCode> = new Set<PlaybackErrorCode>([
  'TOO_EARLY',
  'UNAVAILABLE',
  'RATE_LIMITED',
  'NETWORK',
]);

/** True for codes the wait-for-live loop should keep polling on. */
export function isWaitable(code: PlaybackErrorCode): boolean {
  return WAITABLE.has(code);
}
